/**
 * The contract between the backend SSR renderer and the browser. The server
 * writes the payload as `<script type="application/json" id="xq-ssr-data">`
 * — a data block, because prod CSP (script-src 'self') blocks inline JS — and
 * only on server-rendered marketing pages. When it is present main.tsx
 * hydrates the marketing tree instead of booting the app.
 */

import type { DehydratedState } from '@tanstack/react-query'

/** Must match SSR_DATA_ID in backend/src/services/ssr/marketing-ssr.ts. */
export const SSR_DATA_ID = 'xq-ssr-data'

export type SsrPrefetch = {
  siteContent: unknown
  cmsNavPages: unknown
  pricingLadder: unknown
  /** Set only when rendering a CMS ('company') page. */
  cmsPage?: { slug: string; data: unknown }
}

export type SsrPayload = {
  /** Dehydrated react-query cache, keyed exactly like the client hooks. */
  state: DehydratedState
}

/** Reads the server payload, or null on any page the server did not render.
 *  A malformed block also returns null: the app then boots normally. */
export function readSsrPayload(): SsrPayload | null {
  const el = document.getElementById(SSR_DATA_ID)
  if (!el?.textContent) return null
  try {
    return JSON.parse(el.textContent) as SsrPayload
  } catch {
    return null
  }
}
